import "../questionnaire-templates.scss";

import axios from "axios";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { generatePath, NavLink, useHistory } from "react-router-dom";

import { QuestionnaireAPI } from "@/api/legacy/risk-assessment/QuestionnaireApi";
import { Icon } from "@/components/Elements";
import { formatPercentage } from "@/components/helpers/number-formatters";
import Loading from "@/components/Loading";
import { useMainStore } from "@/contexts/Store";
import {
  CW_QUESTIONNAIRE_COMPLETED,
  CW_QUESTIONNAIRE_IN_REVIEW,
  CW_QUESTIONNAIRE_SUBMITTED,
  ROUTES,
} from "@/features/risk-assessment";
import ModuleTabs from "@/features/risk-assessment/components/ModuleTabs/ModuleTabs";
import RowOptions from "@/features/risk-assessment/components/RowOptions/RowOptions";
import ActionCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ActionCell";
import ContentCell from "@/features/risk-assessment/components/Table/GenericTable/Cells/ContentCell";
import Table from "@/features/risk-assessment/components/Table/GenericTable/Table";
import TableCellSpacer from "@/features/risk-assessment/components/Table/GenericTable/TableCellSpacer";
import TableHeaderCell from "@/features/risk-assessment/components/Table/GenericTable/TableHeaderCell";
import TableRow from "@/features/risk-assessment/components/Table/GenericTable/TableRow";
import { CWQuestionnaireRead } from "@/features/risk-assessment/types/questionnaire";

import DashboardContent from "../../../../components/shared/DashboardContent";
import DashboardContentWrapper from "../../../../components/shared/DashboardContentWrapper";
import DashboardHeader from "../../../../components/shared/DashboardHeader";
import EmptyMessage from "../../components/EmptyMessage/EmptyMessage";
import { ModuleContentWrapper } from "../../components/ModuleContentWrapper/ModuleContentWrapper";
import ModuleHeader from "../../components/ModuleHeader/ModuleHeader";

function PartnerQuestionnairePage() {
  const mainStore = useMainStore();
  const { workspaceID } = mainStore.context;
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<CWQuestionnaireRead[]>([]);

  const moduleWorkspaceID = mainStore.moduleWorkspaces.list.find(
    (item) => item.themis_module.identifier === "risk_assessment",
  )?.id;
  const internalWorkspace = !!mainStore.workspaces.list.find(
    (ws) => ws.is_internal,
  );
  const isInternalUser = !!internalWorkspace;

  const loadTemplates = async (passedModuleWorkspaceID?: number) => {
    if (!passedModuleWorkspaceID) {
      return;
    }

    const { questionnaires } = await QuestionnaireAPI.getAll(
      passedModuleWorkspaceID,
    );
    setList(questionnaires as CWQuestionnaireRead[]);
    setLoading(false);
  };

  const generateOptions = (item: CWQuestionnaireRead) => {
    const cwUserOptions = [];
    if (isInternalUser) {
      if (
        [CW_QUESTIONNAIRE_SUBMITTED, CW_QUESTIONNAIRE_IN_REVIEW].includes(
          item.status,
        )
      ) {
        cwUserOptions.push({
          label: "Review Questionnaire",
          onClick: () => onReview(item),
        });
      }
      cwUserOptions.push({
        label: "View Questionnaire Review Page",
        onClick: () => viewPartnerReviewPage(item.assessment_id),
      });
    } else {
      if (
        item.completion_percentage === 1 &&
        ![
          CW_QUESTIONNAIRE_SUBMITTED,
          CW_QUESTIONNAIRE_IN_REVIEW,
          CW_QUESTIONNAIRE_COMPLETED,
        ].includes(item.status)
      ) {
        cwUserOptions.push({
          label: "Submit",
          onClick: () => submitQuestionnaire(item.id),
        });
      }

      cwUserOptions.push({
        label: `${
          [
            CW_QUESTIONNAIRE_SUBMITTED,
            CW_QUESTIONNAIRE_IN_REVIEW,
            CW_QUESTIONNAIRE_COMPLETED,
          ].includes(item.status)
            ? "View"
            : "Answer"
        } Questionnaire`,
        onClick: () =>
          viewQuestionnaire(item.external_id, item.assessment_id, item.id),
      });
    }

    return cwUserOptions;
  };

  const submitQuestionnaire = async (questionnaireID: number) => {
    try {
      await QuestionnaireAPI.saveAnswers(questionnaireID, [], true);
      const newList = [...list];
      const questionnaireIndex = list.findIndex(
        (questionnaire) => questionnaire.id === questionnaireID,
      );
      newList[questionnaireIndex].status = "Submitted";
      setList(newList);
      mainStore.toast.setInfoText("Questionnaire successfully submitted");
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data?.errors?.base?.[0].includes(
          "Not all required questions have been answered",
        )
      ) {
        return mainStore.toast.setErrorText("Please, answer all questions.");
      }
      mainStore.toast.setErrorText("Something went wrong.");
    }
  };
  const viewQuestionnaire = (
    questionaireExternalID: string,
    assessmentID: number,
    questionnaireID: number,
  ) => {
    const redirectionPath = isInternalUser
      ? generatePath(ROUTES.ASSESSMENTS_QUESTIONNAIRE_REVIEW_PATH, {
          assessmentId: assessmentID,
          questionnaireId: questionnaireID,
          workspace_id: Number(workspaceID),
        })
      : generatePath(ROUTES.PARTNERS_QUESTIONNAIRE_REVIEW_PATH, {
          questionnaireId: questionnaireID,
          workspace_id: Number(workspaceID),
        });
    history.push(redirectionPath);
  };

  const onReview = async (questionnaire: CWQuestionnaireRead) => {
    if (questionnaire.status == "Submitted") {
      await QuestionnaireAPI.setInReview(questionnaire.id);
    }

    history.push(
      generatePath(ROUTES.ASSESSMENTS_QUESTIONNAIRE_REVIEW_PATH, {
        assessmentId: questionnaire.assessment_id,
        questionnaireId: questionnaire.id,
        workspace_id: Number(workspaceID),
      }),
    );
  };

  const viewPartnerReviewPage = (assessmentID: number) => {
    history.push(
      generatePath(ROUTES.CUSTOMER_QUESTIONNAIRE_DETAIL_REVIEW_PATH, {
        assessmentId: assessmentID,
        workspace_id: Number(workspaceID),
      }),
    );
  };

  useEffect(() => {
    loadTemplates(moduleWorkspaceID);
  }, [moduleWorkspaceID]);

  if (loading) {
    return <Loading loadingLayout="table" showTableHeader={false} />;
  }

  return (
    <DashboardContent>
      <DashboardHeader title="Questionnaires" />
      <DashboardContentWrapper>
        <ModuleHeader>
          <div className="risk-assessment-tabs">
            <ModuleTabs>
              <NavLink
                exact
                to={`/workspaces/${workspaceID}/modules/risk-assessment/dashboard`}
              >
                Questionnaires
              </NavLink>
              <NavLink
                exact
                to={`/workspaces/${workspaceID}/modules/risk-assessment/documents`}
              >
                Documents
              </NavLink>
            </ModuleTabs>
          </div>
        </ModuleHeader>
        <ModuleContentWrapper className="questionnaire-templates-table-wrapper">
          {list.length === 0 ? (
            <EmptyMessage>No questionnaire available.</EmptyMessage>
          ) : (
            <Table
              header={
                <>
                  <TableHeaderCell
                    title="Status"
                    leftIcon={
                      <Icon color="extrasBlueGrayDarker" name="book1" />
                    }
                    firstDataHeader
                  />
                  <TableHeaderCell
                    title="Name"
                    leftIcon={
                      <Icon color="extrasBlueGrayDarker" name="book1" />
                    }
                  />
                  <TableHeaderCell
                    title="Percentage Answered"
                    leftIcon={
                      <Icon color="extrasBlueGrayDarker" name="number" />
                    }
                    lastDataHeader
                  />
                  <TableCellSpacer />
                </>
              }
            >
              {list.map((item, rowIndex) => {
                return (
                  <TableRow
                    key={item.id}
                    rowIndex={rowIndex}
                    onClick={() => {}}
                  >
                    <ContentCell content={item.status} isFirstDataCell />
                    <ContentCell content={item.name} />
                    <ContentCell
                      content={formatPercentage(item.completion_percentage)}
                    />
                    <ActionCell>
                      <RowOptions options={generateOptions(item)} />
                    </ActionCell>
                  </TableRow>
                );
              })}
            </Table>
          )}
        </ModuleContentWrapper>
      </DashboardContentWrapper>
    </DashboardContent>
  );
}

export default observer(PartnerQuestionnairePage);
